import React, { useState, useEffect } from 'react';
import Header from '../../layouts/Header';
import { toast } from 'react-toastify';
import axios from '../../api/axios';
import { Button, Col, Form, Row, Spinner, Card, Table, Badge, Accordion } from 'react-bootstrap';
import DatePicker from "react-datepicker";
import { format } from 'date-fns';

const AddonSalesSummary = () => {
    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin] = useState(currentSkin);

    const accessToken = localStorage.getItem('authToken');
    const [outlets, setOutlets] = useState([]);
    const [addonSalesSummary, setAddonSalesSummary] = useState([]);
    const [selectedOutlet, setSelectedOutlet] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [subBtnLoading, setSubBtnLoading] = useState(false);
    const [outletName, setOutletName] = useState('');
    const [dateRangeLabel, setDateRangeLabel] = useState('');

    const getOutlets = async () => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            const response = await axios.get("outlet", { headers });
       
            if (response?.data?.status === true) {
                let filteredOutlets = response?.data?.data?.outlets?.filter((act) => act?.status === "ACTIVE");
                setOutlets(filteredOutlets);
            }
        } catch (error) {
            if (error.response && error.response?.data?.status === false) {
                let errorMessage = error.response.data.error;
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }
        }
    };
       
    const getAddonSalesSummary = async (startDate, endDate, outletId) => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            let newUrl = `summary/day-addon-sales-summary?startDate=${startDate?.toISOString()?.split("T")[0]}&endDate=${endDate?.toISOString()?.split("T")[0]}`;
            
            if (outletId) {
                newUrl += `&outletId=${outletId}`;
                const selectedOutlet = outlets.find(outlet => outlet._id === outletId);
                setOutletName(selectedOutlet?.name || '');
            }
            
            const response = await axios.get(newUrl, { headers });
           
            if (response?.data?.status === true) {
                setAddonSalesSummary(response?.data?.data?.reportData || []);
                
                // Set date range label
                if (startDate && endDate) {
                    const start = format(startDate, 'dd MMM yyyy');
                    const end = format(endDate, 'dd MMM yyyy');
                    setDateRangeLabel(start === end ? start : `${start} - ${end}`);
                }
            }
        } catch (error) {
            if (error.response && error.response?.data?.status === false) {
                let errorMessage = error.response.data.error;
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }
        }
    };
   
    const handleSubmit = async() => {
        if (!startDate || !endDate) {
            toast.error("Please select both start and end dates", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
            return;
        }
        
        if (startDate > endDate) {
            toast.error("Start date cannot be after end date", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
            return;
        }
        
        setSubBtnLoading(true);
        const adjustedStartDate = new Date(startDate);
        adjustedStartDate.setHours(0, 0, 0, 0);
        
        const adjustedEndDate = new Date(endDate);
        adjustedEndDate.setHours(23, 59, 59, 999);
        
        await getAddonSalesSummary(adjustedStartDate, adjustedEndDate, selectedOutlet);
        setSubBtnLoading(false);
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return '₹0.00';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(value);
    };

    const getProductSummary = (productName) => {
        const productAddons = addonSalesSummary.filter(item => item.productName === productName);
        if (productAddons.length === 0) return null;
       
        return {
            productName,
            categoryName: productAddons[0].categoryName,
            totalAddons: productAddons.reduce((sum, item) => sum + item.totalAddonSalesCount, 0),
            totalGrandTotal: productAddons.reduce((sum, item) => sum + item.totalGrandTotal, 0),
            totalTaxable: productAddons.reduce((sum, item) => sum + item.totalTaxable, 0),
            totalDiscount: productAddons.reduce((sum, item) => sum + item.totalDiscount, 0),
            totalTaxAmount: productAddons.reduce((sum, item) => sum + item.totalTaxAmount, 0)
        };
    };

    const getUniqueProducts = () => {
        return [...new Set(addonSalesSummary.map(item => item.productName))];
    };

    // Calculate totals for all addons
    const calculateTotals = () => {
        return {
            totalProducts: getUniqueProducts().length,
            totalAddons: addonSalesSummary.reduce((sum, item) => sum + item.totalAddonSalesCount, 0),
            totalRevenue: addonSalesSummary.reduce((sum, item) => sum + item.totalGrandTotal, 0),
            totalTaxable: addonSalesSummary.reduce((sum, item) => sum + item.totalTaxable, 0),
            totalDiscount: addonSalesSummary.reduce((sum, item) => sum + item.totalDiscount, 0),
            totalTax: addonSalesSummary.reduce((sum, item) => sum + item.totalTaxAmount, 0)
        };
    };

    // Group addons by type for the performance summary
    const getAddonTypeStats = () => {
        const addonTypes = {};
        
        addonSalesSummary.forEach(item => {
            if (!addonTypes[item.addonTitle]) {
                addonTypes[item.addonTitle] = {
                    products: new Set(),
                    quantity: 0,
                    revenue: 0
                };
            }
            
            addonTypes[item.addonTitle].products.add(item.productName);
            addonTypes[item.addonTitle].quantity += item.totalAddonSalesCount || 0;
            addonTypes[item.addonTitle].revenue += item.totalGrandTotal || 0;
        });
        
        return Object.entries(addonTypes).map(([name, stats]) => ({
            name,
            productCount: stats.products.size,
            quantity: stats.quantity,
            revenue: stats.revenue,
            percentage: (stats.revenue / calculateTotals().totalRevenue) * 100
        }));
    };

    useEffect(() => {
        getOutlets();
    }, []);
   
    useEffect(() => {
        const now = new Date();
        const currentDate = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate()
        );
        setStartDate(currentDate);
        setEndDate(currentDate);
    }, []);

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div
                className={`main main-app p-3 p-lg-4 ${
                skin === "dark" ? "bg-dark text-light" : "bg-light"
                }`}
            >
                <div className="d-flex flex-column gap-3">
                    <div className={`card ${skin === "dark" ? "bg-dark-dark" : ""} border-0 shadow-sm`}>
                        <div className="card-header bg-white border-0 py-3">
                            <h4 className="mb-0 fw-bold">
                                <i className="bi bi-bag me-2"></i>ADDON SALES SUMMARY
                            </h4>
                        </div>
                        <hr className="mt-0 mb-0" />
             
                        <div className="card-body">
                            {/* Filters Section */}
                            <div className="filters-section p-3 bg-light rounded-3 mb-4">
                                <Row className="g-3">
                                    <Col md={3}>
                                        <Form.Group controlId="outletSelect">
                                            <Form.Label className="fw-semibold">
                                                Select Outlet
                                            </Form.Label>
                                            <Form.Select
                                                value={selectedOutlet}
                                                onChange={(e) => setSelectedOutlet(e.target.value)}
                                                className="border-secondary"
                                            >
                                            <option value="">All Outlets</option>
                                            {outlets.map((outlet, index) => (
                                                <option key={index} value={outlet?._id}>
                                                {outlet?.name}
                                                </option>
                                            ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={2}>
                                        <Form.Group controlId="startDatePicker">
                                            <Form.Label className="fw-semibold">
                                                Start Date <span className="text-danger">*</span>
                                            </Form.Label>
                                            <DatePicker
                                                selected={startDate}
                                                onChange={(date) => setStartDate(date)}
                                                dateFormat="dd-MM-yyyy"
                                                placeholderText="Select Start Date"
                                                className="form-control border-secondary"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={2}>
                                        <Form.Group controlId="endDatePicker">
                                            <Form.Label className="fw-semibold">
                                                End Date <span className="text-danger">*</span>
                                            </Form.Label>
                                            <DatePicker
                                                selected={endDate}
                                                onChange={(date) => setEndDate(date)}
                                                dateFormat="dd-MM-yyyy"
                                                placeholderText="Select End Date"
                                                className="form-control border-secondary"
                                                minDate={startDate}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={2} className="d-flex align-items-end">
                                        <Button
                                            variant="primary"
                                            className="w-100 py-2"
                                            onClick={handleSubmit}
                                            disabled={subBtnLoading || !startDate || !endDate}
                                        >
                                            {subBtnLoading ? (
                                                <Spinner size="sm" animation="border" />
                                                ) : (
                                                "Generate Report"
                                            )}
                                        </Button>
                                    </Col>
                                </Row>
                            </div>

                            {/* Report Section */}
                            {addonSalesSummary.length > 0 && (
                                <div className={`report-section ${skin === "dark" ? "text-light" : ""}`}>
                                    <Card className="mb-4 border-0 shadow-sm">
                                        <Card.Header className="bg-primary text-white">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <h5 className="mb-0">
                                                    Addon Sales Summary {dateRangeLabel && `(${dateRangeLabel})`}
                                                </h5>
                                                <div className="text-end">
                                                    <small className="d-block">{selectedOutlet ? outletName : 'All Outlets'}</small>
                                                    <small className="d-block">
                                                        {Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24) + 1)} days
                                                    </small>
                                                </div>
                                            </div>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="mb-4">
                                                <Row>
                                                    <Col md={4}>
                                                        <Card className="border-0 shadow-sm bg-light">
                                                            <Card.Body className="py-2">
                                                                <div className="d-flex justify-content-between">
                                                                    <span className="fw-semibold">Total Products:</span>
                                                                    <span>{calculateTotals().totalProducts}</span>
                                                                </div>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                    <Col md={4}>
                                                        <Card className="border-0 shadow-sm bg-light">
                                                            <Card.Body className="py-2">
                                                                <div className="d-flex justify-content-between">
                                                                    <span className="fw-semibold">Total Addons Sold:</span>
                                                                    <span>{calculateTotals().totalAddons}</span>
                                                                </div>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                    <Col md={4}>
                                                        <Card className="border-0 shadow-sm bg-light">
                                                            <Card.Body className="py-2">
                                                                <div className="d-flex justify-content-between">
                                                                    <span className="fw-semibold">Total Revenue:</span>
                                                                    <span className="fw-bold text-success">
                                                                        {formatCurrency(calculateTotals().totalRevenue)}
                                                                    </span>
                                                                </div>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                </Row>
                                            </div>

                                            <Accordion defaultActiveKey="0" className="mb-4">
                                                {getUniqueProducts().map((productName, index) => {
                                                    const productSummary = getProductSummary(productName);
                                                    if (!productSummary) return null;
                                                   
                                                    return (
                                                        <Accordion.Item eventKey={index.toString()} key={index}>
                                                            <Accordion.Header>
                                                                <div className="d-flex justify-content-between w-100 pe-3">
                                                                    <span className="fw-bold">{productName}</span>
                                                                    <span className="text-muted">{productSummary.categoryName}</span>
                                                                    <span className="text-success fw-bold">
                                                                        {formatCurrency(productSummary.totalGrandTotal)}
                                                                    </span>
                                                                </div>
                                                            </Accordion.Header>
                                                            <Accordion.Body>
                                                                <div className="mb-3">
                                                                    <Row>
                                                                        <Col md={3}>
                                                                            <div className="d-flex justify-content-between mb-2">
                                                                                <span className="fw-semibold">Total Addons:</span>
                                                                                <span>{productSummary.totalAddons}</span>
                                                                            </div>
                                                                        </Col>
                                                                        <Col md={3}>
                                                                            <div className="d-flex justify-content-between mb-2">
                                                                                <span className="fw-semibold">Taxable Amount:</span>
                                                                                <span>{formatCurrency(productSummary.totalTaxable)}</span>
                                                                            </div>
                                                                        </Col>
                                                                        <Col md={3}>
                                                                            <div className="d-flex justify-content-between mb-2">
                                                                                <span className="fw-semibold">Discount:</span>
                                                                                <span className="text-danger">-{formatCurrency(productSummary.totalDiscount)}</span>
                                                                            </div>
                                                                        </Col>
                                                                        <Col md={3}>
                                                                            <div className="d-flex justify-content-between mb-2">
                                                                                <span className="fw-semibold">Tax:</span>
                                                                                <span>{formatCurrency(productSummary.totalTaxAmount)}</span>
                                                                            </div>
                                                                        </Col>
                                                                    </Row>
                                                                </div>

                                                                <Table bordered hover className="mb-0">
                                                                    <thead className="table-light">
                                                                        <tr>
                                                                            <th>Addon Type</th>
                                                                            <th>Options</th>
                                                                            <th className="text-end">Qty Sold</th>
                                                                            <th className="text-end">Revenue</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {addonSalesSummary
                                                                            .filter(item => item.productName === productName)
                                                                            .map((addon, addonIndex) => (
                                                                                <tr key={addonIndex}>
                                                                                    <td className="fw-semibold">{addon.addonTitle}</td>
                                                                                    <td>
                                                                                        <div className="d-flex flex-wrap gap-1">
                                                                                            {addon.values.map((value, valueIndex) => (
                                                                                                <Badge bg="secondary" key={valueIndex}>
                                                                                                    {value.selectedValue} ({value.totalSaleCount})
                                                                                                </Badge>
                                                                                            ))}
                                                                                        </div>
                                                                                    </td>
                                                                                    <td className="text-end">{addon.totalAddonSalesCount}</td>
                                                                                    <td className="text-end fw-bold text-success">
                                                                                        {formatCurrency(addon.totalGrandTotal)}
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                    </tbody>
                                                                </Table>
                                                            </Accordion.Body>
                                                        </Accordion.Item>
                                                    );
                                                })}
                                            </Accordion>

                                            <Card className="border-0 shadow-sm">
                                                <Card.Header className="bg-light">
                                                    <h6 className="mb-0 fw-semibold">Addon Performance Summary</h6>
                                                </Card.Header>
                                                <Card.Body>
                                                    <Table bordered hover>
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th>Addon Type</th>
                                                                <th className="text-end">Products</th>
                                                                <th className="text-end">Qty Sold</th>
                                                                <th className="text-end">Revenue</th>
                                                                <th className="text-end">% of Total</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {getAddonTypeStats().map((addon, index) => (
                                                                <tr key={index}>
                                                                    <td className="fw-semibold">{addon.name}</td>
                                                                    <td className="text-end">{addon.productCount}</td>
                                                                    <td className="text-end">{addon.quantity}</td>
                                                                    <td className="text-end fw-bold text-success">
                                                                        {formatCurrency(addon.revenue)}
                                                                    </td>
                                                                    <td className="text-end">{addon.percentage.toFixed(1)}%</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                        <tfoot className="table-active">
                                                            <tr>
                                                                <th className="text-end" colSpan="2">Totals:</th>
                                                                <th className="text-end">{calculateTotals().totalAddons}</th>
                                                                <th className="text-end fw-bold text-success">
                                                                    {formatCurrency(calculateTotals().totalRevenue)}
                                                                </th>
                                                                <th className="text-end">100%</th>
                                                            </tr>
                                                        </tfoot>
                                                    </Table>
                                                </Card.Body>
                                            </Card>
                                        </Card.Body>
                                        <Card.Footer className="text-muted">
                                            <small>Summary generated on {format(new Date(), 'dd MMMM yyyy hh:mm a')}</small>
                                        </Card.Footer>
                                    </Card>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    )
}

export default AddonSalesSummary

